document.documentElement.classList.remove('no-js');

const setDrawerState = (drawer, isOpen, rootClass) => {
  if (!drawer) return;
  drawer.hidden = false;
  drawer.setAttribute('aria-hidden', String(!isOpen));
  document.documentElement.classList.toggle(rootClass, isOpen);
  if (!isOpen && drawer.hasAttribute('hidden')) return;
  if (!isOpen) {
    window.setTimeout(() => {
      if (drawer.getAttribute('aria-hidden') === 'true') {
        drawer.hidden = true;
      }
    }, 220);
  }
};

const closeUtilityDrawers = () => {
  setDrawerState(document.querySelector('[data-search-drawer]'), false, 'search-drawer-open');
  setDrawerState(document.querySelector('[data-localization-drawer]'), false, 'localization-drawer-open');
};

const setCartDrawerState = (drawer, isOpen) => {
  if (!drawer) return;
  drawer.hidden = false;
  drawer.setAttribute('aria-hidden', String(!isOpen));
  document.documentElement.classList.toggle('cart-drawer-open', isOpen);

  if (isOpen) {
    drawer.querySelector('[data-cart-drawer-close]')?.focus({ preventScroll: true });
    return;
  }

  window.setTimeout(() => {
    if (drawer.getAttribute('aria-hidden') === 'true') {
      drawer.hidden = true;
    }
  }, 220);
};

const getProductGalleryIndex = (gallery) => {
  const track = gallery?.querySelector('[data-product-media-track]');
  const mediaItems = Array.from(gallery?.querySelectorAll('[data-product-media]') || []);
  if (!track || !mediaItems.length) return 0;
  const storedIndex = Number.parseInt(gallery.dataset.currentMediaIndex || '', 10);
  if (
    Number.isFinite(storedIndex) &&
    storedIndex >= 0 &&
    storedIndex < mediaItems.length &&
    track.dataset.galleryProgrammaticScroll === 'true'
  ) {
    return storedIndex;
  }
  const trackRect = track.getBoundingClientRect();
  const trackCenter = trackRect.left + trackRect.width / 2;
  return mediaItems.reduce((closestIndex, item, index) => {
    const closestRect = mediaItems[closestIndex].getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const closestDistance = Math.abs(closestRect.left + closestRect.width / 2 - trackCenter);
    const itemDistance = Math.abs(itemRect.left + itemRect.width / 2 - trackCenter);
    return itemDistance < closestDistance ? index : closestIndex;
  }, 0);
};

const pauseProductMediaElement = (element) => {
  if (!element) return;
  element.querySelectorAll('video').forEach((video) => {
    try {
      video.pause();
    } catch (error) {
      // Some browser media states are not pausable yet.
    }
  });
  element.querySelectorAll('iframe').forEach((iframe) => {
    try {
      iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
      iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
    } catch (error) {
      // Cross-origin video embeds may ignore pause commands.
    }
  });
};

const prepareProductGalleryVideo = (video) => {
  if (!video) return;
  video.muted = true;
  video.loop = true;
  video.playsInline = true;
  video.autoplay = true;
  video.controls = false;
  video.preload = 'auto';
  video.setAttribute('muted', '');
  video.setAttribute('loop', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('autoplay', '');
  video.removeAttribute('controls');
};

const playProductMediaElement = (element) => {
  if (!element) return;
  element.querySelectorAll('video').forEach((video) => {
    prepareProductGalleryVideo(video);
    try {
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } catch (error) {
      // Browsers may delay autoplay until the media is ready.
    }
  });
  element.querySelectorAll('iframe').forEach((iframe) => {
    try {
      iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'playVideo', args: [] }), '*');
      iframe.contentWindow?.postMessage(JSON.stringify({ method: 'play' }), '*');
    } catch (error) {
      // Cross-origin embeds may ignore play commands until their API is ready.
    }
  });
};

const pauseInactiveProductGalleryMedia = (gallery, activeIndex = -1) => {
  Array.from(gallery?.querySelectorAll('[data-product-media]') || []).forEach((item, index) => {
    if (index !== activeIndex) pauseProductMediaElement(item);
  });
};

const updateActiveProductGalleryMedia = (gallery, activeIndex = -1) => {
  pauseInactiveProductGalleryMedia(gallery, activeIndex);
  const mediaItems = Array.from(gallery?.querySelectorAll('[data-product-media]') || []);
  playProductMediaElement(mediaItems[activeIndex]);
};

const enableProductExternalVideoApi = (gallery) => {
  gallery?.querySelectorAll('.product__media-video iframe').forEach((iframe) => {
    const src = iframe.getAttribute('src');
    if (!src) return;

    let url;
    try {
      url = new URL(src, window.location.origin);
    } catch (error) {
      return;
    }

    let changed = false;
    if (url.hostname.includes('youtube.com')) {
      url.searchParams.set('enablejsapi', '1');
      url.searchParams.set('autoplay', '0');
      url.searchParams.set('controls', '0');
      url.searchParams.set('mute', '1');
      url.searchParams.set('playsinline', '1');
      url.searchParams.set('rel', '0');
      url.searchParams.set('modestbranding', '1');
      changed = true;
    }
    if (url.hostname.includes('vimeo.com')) {
      url.searchParams.set('api', '1');
      url.searchParams.set('autoplay', '0');
      url.searchParams.set('muted', '1');
      url.searchParams.set('loop', '1');
      url.searchParams.set('controls', '0');
      url.searchParams.set('title', '0');
      url.searchParams.set('byline', '0');
      url.searchParams.set('portrait', '0');
      changed = true;
    }
    if (changed) iframe.setAttribute('src', url.toString());
  });
};

const setProductGalleryIndex = (gallery, index, behavior = 'smooth') => {
  const track = gallery?.querySelector('[data-product-media-track]');
  const mediaItems = Array.from(gallery?.querySelectorAll('[data-product-media]') || []);
  if (!track || !mediaItems.length) return;
  const safeIndex = (index + mediaItems.length) % mediaItems.length;
  const target = mediaItems[safeIndex];
  gallery.dataset.currentMediaIndex = String(safeIndex);
  track.dataset.galleryProgrammaticScroll = 'true';
  syncProductGalleryState(gallery, safeIndex, { scrollThumbnail: true, behavior });
  const firstOffset = mediaItems[0]?.offsetLeft || 0;
  track.scrollTo({ left: target.offsetLeft - firstOffset, behavior });
  window.setTimeout(() => {
    if (gallery.dataset.currentMediaIndex === String(safeIndex)) {
      track.dataset.galleryProgrammaticScroll = 'false';
    }
  }, behavior === 'auto' ? 80 : 420);
};

const syncProductGalleryState = (gallery, index, options = {}) => {
  const mediaItems = Array.from(gallery?.querySelectorAll('[data-product-media]') || []);
  if (!gallery || !mediaItems.length) return;
  const safeIndex = Math.min(Math.max(index, 0), mediaItems.length - 1);
  const target = mediaItems[safeIndex];
  const behavior = options.behavior || 'auto';
  gallery.dataset.currentMediaIndex = String(safeIndex);
  updateActiveProductGalleryMedia(gallery, safeIndex);
  updateProductGalleryCounter(gallery, safeIndex);
  gallery.querySelectorAll('[data-product-thumbnail]').forEach((thumbnail) => {
    const isActive = thumbnail.dataset.targetMedia === target.id;
    thumbnail.classList.toggle('is-active', isActive);
    if (isActive && options.scrollThumbnail) {
      thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior });
    }
  });
};

const updateProductGalleryCounter = (gallery, index = getProductGalleryIndex(gallery)) => {
  const counter = gallery?.querySelector('[data-product-gallery-counter]');
  const current = counter?.querySelector('[data-product-gallery-current]');
  const total = counter?.querySelector('[data-product-gallery-total]');
  const mediaItems = gallery?.querySelectorAll('[data-product-media]');
  if (!counter || !current || !total || !mediaItems?.length) return;
  const safeIndex = Math.min(Math.max(index, 0), mediaItems.length - 1);
  current.textContent = String(safeIndex + 1);
  total.textContent = String(mediaItems.length);
};

const initializeProductGallerySwipe = (gallery, track) => {
  if (!gallery || !track || track.dataset.gallerySwipeInitialized === 'true') return;
  track.dataset.gallerySwipeInitialized = 'true';

  let startX = 0;
  let startY = 0;
  let startScrollLeft = 0;
  let startIndex = 0;
  let isDragging = false;

  track.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    startX = event.clientX;
    startY = event.clientY;
    startScrollLeft = track.scrollLeft;
    startIndex = getProductGalleryIndex(gallery);
    isDragging = true;
    track.dataset.galleryUserSwipe = 'true';
    track.setPointerCapture?.(event.pointerId);
  }, { passive: true });

  track.addEventListener('pointermove', (event) => {
    if (!isDragging) return;
    const deltaX = event.clientX - startX;
    const deltaY = event.clientY - startY;
    if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
    event.preventDefault();
    track.scrollLeft = startScrollLeft - deltaX;
  });

  const finishSwipe = (event) => {
    if (!isDragging) return;
    isDragging = false;
    track.releasePointerCapture?.(event.pointerId);

    const deltaX = event.clientX - startX;
    const deltaY = event.clientY - startY;
    const shouldChangeSlide = Math.abs(deltaX) > 42 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2;
    const nextIndex = shouldChangeSlide ? startIndex + (deltaX < 0 ? 1 : -1) : startIndex;
    if (shouldChangeSlide) gallery.dataset.ignoreNextLightboxClick = 'true';
    setProductGalleryIndex(gallery, nextIndex);

    window.setTimeout(() => {
      track.dataset.galleryUserSwipe = 'false';
      gallery.dataset.ignoreNextLightboxClick = 'false';
    }, 180);
  };

  track.addEventListener('pointerup', finishSwipe);
  track.addEventListener('pointercancel', finishSwipe);
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const setProductLightboxImageAspect = (lightbox, image) => {
  if (!lightbox || !image) return;
  const applyAspect = () => {
    if (!image.naturalWidth || !image.naturalHeight) return;
    lightbox.style.setProperty('--product-lightbox-aspect', String(image.naturalWidth / image.naturalHeight));
  };

  if (image.complete) {
    applyAspect();
    return;
  }

  image.addEventListener('load', applyAspect, { once: true });
};

const getProductLightboxZoomScale = (lightbox) => {
  const rawScale = window.getComputedStyle(lightbox).getPropertyValue('--product-lightbox-zoom-scale');
  const scale = Number.parseFloat(rawScale);
  return Number.isFinite(scale) && scale > 1 ? scale : 1.85;
};

const getProductLightboxPanBounds = (lightbox) => {
  const viewport = lightbox?.querySelector('.product-lightbox__viewport');
  const image = lightbox?.querySelector('[data-product-lightbox-image]');
  if (!viewport || !image) return { x: 0, y: 0 };
  const scale = getProductLightboxZoomScale(lightbox);
  return {
    x: Math.max(0, ((image.offsetWidth || 0) * scale - viewport.clientWidth) / 2),
    y: Math.max(0, ((image.offsetHeight || 0) * scale - viewport.clientHeight) / 2),
  };
};

const setProductLightboxPan = (lightbox, x = 0, y = 0) => {
  if (!lightbox) return;
  const bounds = getProductLightboxPanBounds(lightbox);
  const panX = clamp(x, -bounds.x, bounds.x);
  const panY = clamp(y, -bounds.y, bounds.y);
  lightbox.dataset.panX = String(panX);
  lightbox.dataset.panY = String(panY);
  lightbox.style.setProperty('--product-lightbox-pan-x', `${panX}px`);
  lightbox.style.setProperty('--product-lightbox-pan-y', `${panY}px`);
};

const resetProductLightboxPan = (lightbox) => {
  if (!lightbox) return;
  lightbox.dataset.panX = '0';
  lightbox.dataset.panY = '0';
  lightbox.style.setProperty('--product-lightbox-pan-x', '0px');
  lightbox.style.setProperty('--product-lightbox-pan-y', '0px');
  lightbox.classList.remove('is-panning');
};

const setProductLightboxIndex = (lightbox, index) => {
  const gallery = lightbox?.closest('[data-product-gallery]') || document.getElementById(lightbox?.dataset.productGalleryId || '');
  const triggers = Array.from(gallery?.querySelectorAll('[data-product-lightbox-open]') || []);
  const image = lightbox?.querySelector('[data-product-lightbox-image]');
  if (!lightbox || !image || !triggers.length) return;
  const safeIndex = (index + triggers.length) % triggers.length;
  const trigger = triggers[safeIndex];
  const fallbackImage = trigger.querySelector('img');
  lightbox.dataset.currentIndex = String(safeIndex);
  lightbox.style.removeProperty('--product-lightbox-aspect');
  image.src = trigger.dataset.mediaSrc || fallbackImage?.currentSrc || fallbackImage?.src || '';
  image.alt = trigger.dataset.mediaAlt || fallbackImage?.alt || '';
  setProductLightboxImageAspect(lightbox, image);
  lightbox.querySelector('[data-product-lightbox-current]').textContent = String(safeIndex + 1);
  lightbox.classList.remove('is-zoomed');
  resetProductLightboxPan(lightbox);
  const zoomButton = lightbox.querySelector('[data-product-lightbox-zoom]');
  zoomButton?.setAttribute('aria-pressed', 'false');
  zoomButton?.setAttribute('aria-label', 'Zoom image');
  const galleryIndex = Number.parseInt(trigger.dataset.mediaGalleryIndex || String(safeIndex), 10);
  setProductGalleryIndex(gallery, Number.isFinite(galleryIndex) ? galleryIndex : safeIndex, 'smooth');
};

const closeProductLightbox = (lightbox) => {
  if (!lightbox) return;
  lightbox.setAttribute('aria-hidden', 'true');
  lightbox.hidden = true;
  lightbox.classList.remove('is-zoomed');
  resetProductLightboxPan(lightbox);
  document.documentElement.classList.remove('product-lightbox-open');
};

const toggleProductLightboxZoom = (lightbox) => {
  if (!lightbox) return;
  const isZoomed = lightbox.classList.toggle('is-zoomed');
  resetProductLightboxPan(lightbox);
  const zoomButton = lightbox.querySelector('[data-product-lightbox-zoom]');
  zoomButton?.setAttribute('aria-pressed', String(isZoomed));
  zoomButton?.setAttribute('aria-label', isZoomed ? 'Zoom out image' : 'Zoom image');
};

document.addEventListener('click', (event) => {
  const menuToggle = event.target.closest('[data-menu-toggle]');
  if (menuToggle) {
    const header = menuToggle.closest('[data-header]');
    const isOpen = header.getAttribute('data-open') === 'true';
    header.setAttribute('data-open', String(!isOpen));
    menuToggle.setAttribute('aria-expanded', String(!isOpen));
  }

  const mobileSubmenuToggle = event.target.closest('[data-mobile-submenu-toggle]');
  if (mobileSubmenuToggle) {
    const navItem = mobileSubmenuToggle.closest('.header__nav-item');
    const isOpen = navItem?.classList.toggle('is-open');
    mobileSubmenuToggle.setAttribute('aria-expanded', String(Boolean(isOpen)));
  }

  const footerToggle = event.target.closest('[data-footer-toggle]');
  if (footerToggle) {
    const column = footerToggle.closest('[data-footer-accordion]');
    const isOpen = column?.classList.toggle('is-open');
    footerToggle.setAttribute('aria-expanded', String(Boolean(isOpen)));
  }

  const summary = event.target.closest('[data-accordion-summary]');
  if (summary) {
    const item = summary.closest('[data-accordion-item]');
    if (!item) return;
    const isOpen = item.hasAttribute('open');
    const scienceFaqPath = /\/pages\/(science|human-sequence-type-iii-collagen|human-sequence-type-3-collagen|humanized-type-iii-collagen)\/?$/i.test(window.location.pathname);
    const faqScope = item.closest('.faq-preview, .faq-page-section, .science-faq, .product-faq')
      || (scienceFaqPath ? item.closest('.shopify-section, .section, .accordion') : null);
    if (faqScope && !isOpen) {
      faqScope.querySelectorAll('[data-accordion-item][open]').forEach((openItem) => {
        if (openItem === item) return;
        openItem.removeAttribute('open');
        const openSummary = openItem.querySelector('[data-accordion-summary]');
        openSummary?.setAttribute('aria-expanded', 'false');
      });
    }
    item.toggleAttribute('open', !isOpen);
    summary.setAttribute('aria-expanded', String(!isOpen));
  }

  const drawerOpen = event.target.closest('[data-cart-drawer-open], [data-cart-open]');
  if (drawerOpen) {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (drawer) {
      event.preventDefault();
      setCartDrawerState(drawer, true);
    }
  }

  const drawerClose = event.target.closest('[data-cart-drawer-close], [data-cart-close]');
  if (drawerClose) {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (drawer) {
      setCartDrawerState(drawer, false);
    }
  }

  const searchOpen = event.target.closest('[data-search-open]');
  if (searchOpen) {
    const drawer = document.querySelector('[data-search-drawer]');
    if (drawer) {
      event.preventDefault();
      closeUtilityDrawers();
      setDrawerState(drawer, true, 'search-drawer-open');
      drawer.querySelector('[data-search-input]')?.focus();
    }
  }

  const searchClose = event.target.closest('[data-search-close]');
  if (searchClose) {
    setDrawerState(document.querySelector('[data-search-drawer]'), false, 'search-drawer-open');
  }

  const localizationOpen = event.target.closest('[data-localization-open]');
  if (localizationOpen) {
    const drawer = document.querySelector('[data-localization-drawer]');
    if (drawer) {
      event.preventDefault();
      closeUtilityDrawers();
      setDrawerState(drawer, true, 'localization-drawer-open');
    }
  }

  const localizationClose = event.target.closest('[data-localization-close]');
  if (localizationClose) {
    setDrawerState(document.querySelector('[data-localization-drawer]'), false, 'localization-drawer-open');
  }

  const gateAccept = event.target.closest('[data-us-gate-accept]');
  if (gateAccept) {
    const gate = document.querySelector('[data-us-market-gate]');
    window.localStorage.setItem('colsigen-us-market-gate', 'accepted');
    if (gate) {
      gate.setAttribute('aria-hidden', 'true');
      gate.hidden = true;
      document.documentElement.classList.remove('market-gate-open');
    }
  }

  const gateDecline = event.target.closest('[data-us-gate-decline]');
  if (gateDecline) {
    const gate = document.querySelector('[data-us-market-gate]');
    const redirectUrl = gate?.dataset.redirectUrl;
    if (redirectUrl) window.location.href = redirectUrl;
  }

  const galleryThumbnail = event.target.closest('[data-product-thumbnail]');
  if (galleryThumbnail) {
    const gallery = galleryThumbnail.closest('[data-product-gallery]');
    const mediaItems = Array.from(gallery?.querySelectorAll('[data-product-media]') || []);
    const targetIndex = mediaItems.findIndex((item) => item.id === galleryThumbnail.dataset.targetMedia);
    if (targetIndex >= 0) {
      event.preventDefault();
      setProductGalleryIndex(gallery, targetIndex);
    }
  }

  const galleryArrow = event.target.closest('[data-product-gallery-arrow]');
  if (galleryArrow) {
    const gallery = galleryArrow.closest('[data-product-gallery]');
    const mediaItems = Array.from(gallery?.querySelectorAll('[data-product-media]') || []);
    if (mediaItems.length > 1) {
      event.preventDefault();
      const currentIndex = getProductGalleryIndex(gallery);
      const direction = galleryArrow.dataset.productGalleryArrow === 'prev' ? -1 : 1;
      setProductGalleryIndex(gallery, currentIndex + direction);
    }
  }

  const lightboxOpen = event.target.closest('[data-product-lightbox-open]');
  if (lightboxOpen) {
    const gallery = lightboxOpen.closest('[data-product-gallery]');
    const lightbox = gallery?.querySelector('[data-product-lightbox]') || document.getElementById(gallery?.dataset.productLightboxId || '');
    if (gallery?.dataset.ignoreNextLightboxClick === 'true') {
      event.preventDefault();
      gallery.dataset.ignoreNextLightboxClick = 'false';
      return;
    }
    if (lightbox) {
      event.preventDefault();
      document.body.appendChild(lightbox);
      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('product-lightbox-open');
      setProductLightboxIndex(lightbox, Number(lightboxOpen.dataset.mediaIndex || 0));
      lightbox.querySelector('[data-product-lightbox-close]')?.focus();
    }
  }

  const lightboxClose = event.target.closest('[data-product-lightbox-close]');
  if (lightboxClose) {
    closeProductLightbox(lightboxClose.closest('[data-product-lightbox]'));
  }

  const lightbox = event.target.matches('[data-product-lightbox]') ? event.target : event.target.closest('[data-product-lightbox]');
  if (event.target.matches('[data-product-lightbox]')) {
    closeProductLightbox(event.target);
  }

  const lightboxArrow = event.target.closest('[data-product-lightbox-arrow]');
  if (lightboxArrow && lightbox) {
    const currentIndex = Number(lightbox.dataset.currentIndex || 0);
    const direction = lightboxArrow.dataset.productLightboxArrow === 'prev' ? -1 : 1;
    setProductLightboxIndex(lightbox, currentIndex + direction);
  }

  const lightboxZoom = event.target.closest('[data-product-lightbox-zoom]');
  if (lightboxZoom && lightbox) {
    toggleProductLightboxZoom(lightbox);
  }

  const lightboxImage = event.target.closest('[data-product-lightbox-image]');
  if (lightboxImage && lightbox) {
    if (!lightbox.dataset.wasPanning) {
      toggleProductLightboxZoom(lightbox);
    }
    lightbox.removeAttribute('data-was-panning');
  }
});

document.addEventListener('pointerdown', (event) => {
  const image = event.target.closest('[data-product-lightbox-image]');
  const lightbox = image?.closest('[data-product-lightbox]');
  if (!image || !lightbox) return;
  if (!lightbox.classList.contains('is-zoomed')) {
    lightbox.dataset.swipeStartX = String(event.clientX);
    lightbox.dataset.swipeStartY = String(event.clientY);
    lightbox.removeAttribute('data-was-panning');
    lightbox.classList.add('is-swiping');
    return;
  }
  event.preventDefault();
  lightbox.dataset.panStartX = String(event.clientX);
  lightbox.dataset.panStartY = String(event.clientY);
  lightbox.dataset.panOriginX = lightbox.dataset.panX || '0';
  lightbox.dataset.panOriginY = lightbox.dataset.panY || '0';
  lightbox.removeAttribute('data-was-panning');
  lightbox.classList.add('is-panning');
  image.setPointerCapture?.(event.pointerId);
});

document.addEventListener('pointermove', (event) => {
  const lightbox = document.querySelector('[data-product-lightbox].is-panning');
  if (!lightbox) return;
  event.preventDefault();
  const startX = Number.parseFloat(lightbox.dataset.panStartX || '0');
  const startY = Number.parseFloat(lightbox.dataset.panStartY || '0');
  const originX = Number.parseFloat(lightbox.dataset.panOriginX || '0');
  const originY = Number.parseFloat(lightbox.dataset.panOriginY || '0');
  const deltaX = event.clientX - startX;
  const deltaY = event.clientY - startY;
  if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
    lightbox.dataset.wasPanning = 'true';
  }
  setProductLightboxPan(lightbox, originX + deltaX, originY + deltaY);
});

const stopProductLightboxPan = (event) => {
  const lightbox = document.querySelector('[data-product-lightbox].is-panning');
  if (lightbox) {
    const image = lightbox.querySelector('[data-product-lightbox-image]');
    image?.releasePointerCapture?.(event.pointerId);
    lightbox.classList.remove('is-panning');
  }

  const swipeLightbox = document.querySelector('[data-product-lightbox].is-swiping');
  if (!swipeLightbox) return;
  const startX = Number.parseFloat(swipeLightbox.dataset.swipeStartX || '0');
  const startY = Number.parseFloat(swipeLightbox.dataset.swipeStartY || '0');
  const deltaX = event.clientX - startX;
  const deltaY = event.clientY - startY;
  swipeLightbox.classList.remove('is-swiping');
  delete swipeLightbox.dataset.swipeStartX;
  delete swipeLightbox.dataset.swipeStartY;
  if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
    swipeLightbox.dataset.wasPanning = 'true';
    const currentIndex = Number(swipeLightbox.dataset.currentIndex || 0);
    setProductLightboxIndex(swipeLightbox, currentIndex + (deltaX < 0 ? 1 : -1));
  } else if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) {
    swipeLightbox.dataset.wasPanning = 'true';
  }
};

document.addEventListener('pointerup', stopProductLightboxPan);
document.addEventListener('pointercancel', stopProductLightboxPan);

window.addEventListener('resize', () => {
  document.querySelectorAll('[data-product-lightbox].is-zoomed').forEach((lightbox) => {
    setProductLightboxPan(
      lightbox,
      Number.parseFloat(lightbox.dataset.panX || '0'),
      Number.parseFloat(lightbox.dataset.panY || '0')
    );
  });
});

document.addEventListener('keydown', (event) => {
  const openLightbox = document.querySelector('[data-product-lightbox][aria-hidden="false"]');
  if (openLightbox && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
    event.preventDefault();
    const currentIndex = Number(openLightbox.dataset.currentIndex || 0);
    setProductLightboxIndex(openLightbox, currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
    return;
  }

  if (event.key === 'Escape') {
    closeProductLightbox(openLightbox);
    closeUtilityDrawers();
    document.querySelectorAll('[data-header][data-open="true"]').forEach((header) => {
      header.setAttribute('data-open', 'false');
      header.querySelector('[data-menu-toggle]')?.setAttribute('aria-expanded', 'false');
    });
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    if (cartDrawer) {
      setCartDrawerState(cartDrawer, false);
    }
  }
});

const initializeProductGalleries = () => {
  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const track = gallery.querySelector('[data-product-media-track]');
    if (!track || track.dataset.gallerySyncInitialized) return;
    track.dataset.gallerySyncInitialized = 'true';
    enableProductExternalVideoApi(gallery);
    gallery.querySelectorAll('.product__media-video video').forEach(prepareProductGalleryVideo);
    updateActiveProductGalleryMedia(gallery, getProductGalleryIndex(gallery));
    updateProductGalleryCounter(gallery, getProductGalleryIndex(gallery));
    initializeProductGallerySwipe(gallery, track);
    let syncTimer;
    track.addEventListener('scroll', () => {
      window.clearTimeout(syncTimer);
      syncTimer = window.setTimeout(() => {
        if (track.dataset.galleryProgrammaticScroll === 'true') return;
        const galleryIndex = getProductGalleryIndex(gallery);
        syncProductGalleryState(gallery, galleryIndex);
      }, 120);
    }, { passive: true });
  });
};

const initializeViewportAutoplayVideos = () => {
  const videos = Array.from(document.querySelectorAll('.product__media-video video, .application-video video, .clinical-results-slider__video'));
  if (!videos.length || !('IntersectionObserver' in window)) {
    videos.forEach((video) => prepareProductGalleryVideo(video));
    return;
  }

  if (window.__colsigenViewportVideoObserver) {
    videos.forEach((video) => {
      if (video.dataset.viewportVideoObserved === 'true') return;
      video.dataset.viewportVideoObserved = 'true';
      window.__colsigenViewportVideoObserver.observe(video);
    });
    return;
  }

  window.__colsigenViewportVideoObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target;
      const isApplicationVideo = Boolean(video.closest('.application-video'));
      prepareProductGalleryVideo(video);
      if (isApplicationVideo) {
        video.preload = 'auto';
        if (video.readyState < 2) {
          try {
            video.load();
          } catch (error) {
            // Ignore browser media preload errors.
          }
        }
      }

      if (entry.isIntersecting && (entry.intersectionRatio > 0.12 || isApplicationVideo)) {
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      } else {
        try {
          video.pause();
        } catch (error) {
          // Ignore browser media state errors.
        }
      }
    });
  }, { rootMargin: '420px 0px', threshold: [0, 0.12, 0.35, 0.7] });

  videos.forEach((video) => {
    if (video.closest('.application-video')) {
      video.preload = 'auto';
      try {
        video.load();
      } catch (error) {
        // Ignore browser media preload errors.
      }
    }
    video.dataset.viewportVideoObserved = 'true';
    window.__colsigenViewportVideoObserver.observe(video);
  });
};

const getMobileProductSliderIndex = (slider) => {
  const cards = Array.from(slider?.querySelectorAll('.product-card') || []);
  if (!slider || !cards.length) return 0;
  return cards.reduce((closestIndex, card, index) => {
    const closestDistance = Math.abs(cards[closestIndex].offsetLeft - slider.scrollLeft);
    const cardDistance = Math.abs(card.offsetLeft - slider.scrollLeft);
    return cardDistance < closestDistance ? index : closestIndex;
  }, 0);
};

const setMobileProductSliderState = (slider, index) => {
  const section = slider?.closest('.featured-products, .collection-section');
  const nav = section?.querySelector('[data-mobile-product-slider-nav]');
  if (!nav) return;

  const dots = Array.from(nav.querySelectorAll('[data-mobile-product-slider-dot]'));
  const safeIndex = Math.min(Math.max(index, 0), Math.max(dots.length - 1, 0));
  dots.forEach((dot, dotIndex) => {
    const isActive = dotIndex === safeIndex;
    dot.classList.toggle('is-active', isActive);
    dot.setAttribute('aria-current', String(isActive));
  });

  const total = Math.max(dots.length, 1);
  const progressWidth = 100 / total;
  const progressOffset = progressWidth * safeIndex;
  nav.style.setProperty('--mobile-product-slide-count', String(total));
  nav.style.setProperty('--mobile-product-active-index', String(safeIndex));
  nav.style.setProperty('--mobile-product-progress-width', `${progressWidth}%`);
  nav.style.setProperty('--mobile-product-progress-offset', `${progressOffset}%`);
};

const setMobileProductSliderIndex = (slider, index, behavior = 'smooth') => {
  const cards = Array.from(slider?.querySelectorAll('.product-card') || []);
  if (!slider || !cards.length) return;
  const safeIndex = Math.min(Math.max(index, 0), cards.length - 1);
  slider.scrollTo({ left: cards[safeIndex].offsetLeft - slider.offsetLeft, behavior });
  setMobileProductSliderState(slider, safeIndex);
};

const initializeMobileProductSliders = () => {
  document.querySelectorAll('[data-mobile-product-slider]').forEach((slider) => {
    if (slider.dataset.mobileSliderInitialized === 'true') {
      setMobileProductSliderState(slider, getMobileProductSliderIndex(slider));
      return;
    }

    slider.dataset.mobileSliderInitialized = 'true';
    let sliderSyncTimer;

    slider.addEventListener('scroll', () => {
      window.clearTimeout(sliderSyncTimer);
      sliderSyncTimer = window.setTimeout(() => {
        setMobileProductSliderState(slider, getMobileProductSliderIndex(slider));
      }, 80);
    }, { passive: true });

    const nav = slider.closest('.featured-products, .collection-section')?.querySelector('[data-mobile-product-slider-nav]');
    nav?.addEventListener('click', (event) => {
      const dot = event.target.closest('[data-mobile-product-slider-dot]');
      if (!dot) return;
      event.preventDefault();
      setMobileProductSliderIndex(slider, Number.parseInt(dot.dataset.slideIndex || '0', 10));
    });

    nav?.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const currentIndex = getMobileProductSliderIndex(slider);
      const cards = Array.from(slider.querySelectorAll('.product-card'));
      if (event.key === 'Home') {
        setMobileProductSliderIndex(slider, 0);
      } else if (event.key === 'End') {
        setMobileProductSliderIndex(slider, cards.length - 1);
      } else {
        setMobileProductSliderIndex(slider, currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });

    setMobileProductSliderState(slider, getMobileProductSliderIndex(slider));
  });
};

const initializeAnchorDropdowns = () => {
  document.querySelectorAll('[data-anchor-dropdown]').forEach((dropdown) => {
    const toggle = dropdown.querySelector('[data-anchor-dropdown-toggle]');
    const menu = dropdown.querySelector('[data-anchor-dropdown-menu]');
    if (!toggle || !menu || dropdown.dataset.anchorDropdownInitialized === 'true') return;

    dropdown.dataset.anchorDropdownInitialized = 'true';

    const setOpen = (isOpen) => {
      dropdown.classList.toggle('is-open', isOpen);
      toggle.setAttribute('aria-expanded', String(isOpen));
    };

    toggle.addEventListener('click', () => {
      setOpen(!dropdown.classList.contains('is-open'));
    });

    menu.addEventListener('click', (event) => {
      if (event.target.closest('a')) setOpen(false);
    });

    dropdown.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setOpen(false);
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeProductGalleries();
    initializeMobileProductSliders();
    initializeAnchorDropdowns();
    initializeViewportAutoplayVideos();
  });
} else {
  initializeProductGalleries();
  initializeMobileProductSliders();
  initializeAnchorDropdowns();
  initializeViewportAutoplayVideos();
}

document.addEventListener('shopify:section:load', () => {
  initializeProductGalleries();
  initializeMobileProductSliders();
  initializeAnchorDropdowns();
  initializeViewportAutoplayVideos();
});

document.addEventListener('change', (event) => {
  const variantSelect = event.target.closest('[data-variant-select]');
  const variantOption = event.target.closest('[data-variant-option]');
  if (variantSelect || variantOption) {
    const source = variantSelect || variantOption;
    if (source.matches('[data-product-pack-option]') && source.dataset.productUrl) {
      const targetUrl = new URL(source.dataset.productUrl, window.location.origin);
      const currentUrl = new URL(window.location.href);
      if (targetUrl.pathname !== currentUrl.pathname) {
        window.location.href = targetUrl.pathname;
        return;
      }
    }
    const variantId = source.value;
    document.querySelectorAll('[data-variant-id-input]').forEach((input) => {
      input.value = variantId;
    });
    source.closest('form')?.querySelectorAll('.variant-button').forEach((button) => {
      const input = button.querySelector('[data-variant-option]');
      button.classList.toggle('is-selected', Boolean(input && input.checked));
    });
    source.closest('form')?.querySelectorAll('.product-pack-option').forEach((button) => {
      const input = button.querySelector('[data-product-pack-option]');
      button.classList.toggle('is-selected', Boolean(input && input.checked));
    });
    if (source.matches('[data-product-pack-option]') && source.dataset.price) {
      document.querySelectorAll('[data-product-price]').forEach((price) => {
        price.textContent = source.dataset.price;
      });
    }
  }

  const sellingPlanControl = event.target.closest('[data-selling-plan-selector] [data-selling-plan-choice], [data-selling-plan-selector] [data-selling-plan-select]');
  if (!sellingPlanControl) return;

  const selector = sellingPlanControl.closest('[data-selling-plan-selector]');
  const hiddenInput = selector.querySelector('[data-selling-plan-input]');
  const subscriptionRadio = selector.querySelector('[data-selling-plan-subscription]');
  const frequencySelect = selector.querySelector('[data-selling-plan-select]');

  if (sellingPlanControl.matches('[data-selling-plan-select]') && subscriptionRadio) {
    subscriptionRadio.checked = true;
  }

  selector.querySelectorAll('[data-selling-plan-card]').forEach((card) => {
    const radio = card.querySelector('[data-selling-plan-choice]');
    card.classList.toggle('is-selected', Boolean(radio && radio.checked));
  });

  if (hiddenInput) {
    hiddenInput.value = subscriptionRadio && subscriptionRadio.checked && frequencySelect ? frequencySelect.value : '';
  }
});

document.addEventListener('click', (event) => {
  const decrease = event.target.closest('[data-quantity-decrease]');
  const increase = event.target.closest('[data-quantity-increase]');
  if (!decrease && !increase) return;

  const stepper = event.target.closest('[data-quantity-stepper]');
  const input = stepper.querySelector('input[type="number"]');
  const current = Number.parseInt(input.value || '1', 10);
  const next = increase ? current + 1 : Math.max(1, current - 1);

  input.value = String(next);
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

const initRollingTextBanners = () => {
  document.querySelectorAll('[data-rolling-text-banner]').forEach((banner) => {
    if (banner.dataset.rollingTextReady === 'true') return;

    const track = banner.querySelector('[data-rolling-text-track]');
    const group = banner.querySelector('[data-rolling-text-group]');
    if (!track || !group) return;

    banner.dataset.rollingTextReady = 'true';
    banner.classList.add('is-js-marquee');

    let position = 0;
    let previousTime = null;

    const move = (time) => {
      const groupWidth = group.scrollWidth;
      if (groupWidth > 0) {
        const duration = Number.parseFloat(track.dataset.rollingDuration || '14');
        const pixelsPerSecond = groupWidth / Math.max(duration, 1);
        const delta = previousTime === null ? 0 : (time - previousTime) / 1000;

        position = (position + pixelsPerSecond * delta) % groupWidth;
        track.style.transform = `translate3d(${-position}px, 0, 0)`;
      }

      previousTime = time;
      window.requestAnimationFrame(move);
    };

    window.requestAnimationFrame(move);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRollingTextBanners);
} else {
  initRollingTextBanners();
}

document.addEventListener('shopify:section:load', initRollingTextBanners);

const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
let goldMetalScrollTicking = false;

const updateGoldMetalScroll = () => {
  const reduceMotion = reduceMotionQuery.matches;

  document.querySelectorAll('[data-gold-metal-scroll="true"]').forEach((section) => {
    if (reduceMotion) {
      section.style.setProperty('--gold-scroll-shift', '0px');
      return;
    }

    const rect = section.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const total = viewportHeight + rect.height;
    const progress = total > 0 ? (viewportHeight - rect.top) / total : 0.5;
    const clampedProgress = Math.min(1, Math.max(0, progress));
    const range = Number.parseFloat(window.getComputedStyle(section).getPropertyValue('--gold-reflection-range')) || 56;
    const shift = (clampedProgress - 0.5) * range;

    section.style.setProperty('--gold-scroll-shift', `${shift.toFixed(2)}px`);
  });

  goldMetalScrollTicking = false;
};

const requestGoldMetalScrollUpdate = () => {
  if (goldMetalScrollTicking) return;
  goldMetalScrollTicking = true;
  window.requestAnimationFrame(updateGoldMetalScroll);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', updateGoldMetalScroll);
} else {
  updateGoldMetalScroll();
}

window.addEventListener('scroll', requestGoldMetalScrollUpdate, { passive: true });
window.addEventListener('resize', requestGoldMetalScrollUpdate);
reduceMotionQuery.addEventListener?.('change', requestGoldMetalScrollUpdate);
document.addEventListener('shopify:section:load', updateGoldMetalScroll);

const updateHeaderScrollState = () => {
  document.querySelectorAll('[data-header]').forEach((header) => {
    header.classList.toggle('is-scrolled', window.scrollY > 12);
  });
};

window.addEventListener('scroll', updateHeaderScrollState, { passive: true });
updateHeaderScrollState();

const initMarketGate = () => {
  const gate = document.querySelector('[data-us-market-gate]');
  if (!gate || window.localStorage.getItem('colsigen-us-market-gate') === 'accepted') return;

  gate.hidden = false;
  gate.setAttribute('aria-hidden', 'false');
  document.documentElement.classList.add('market-gate-open');
  gate.querySelector('[data-us-gate-accept]')?.focus();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMarketGate);
} else {
  initMarketGate();
}

const formatCartMoney = (cents) => {
  const currency = window.Shopify?.currency?.active || 'USD';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
};

const escapeCartHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const setCartDrawerLoading = (drawer, isLoading) => {
  if (!drawer) return;
  drawer.classList.toggle('is-loading', isLoading);
  drawer.querySelectorAll('[data-cart-quantity-decrease], [data-cart-quantity-increase], [data-cart-quantity-input], [data-cart-remove]')
    .forEach((control) => {
      control.disabled = isLoading;
    });
};

const showCartDrawerMessage = (drawer, message = '') => {
  const messageElement = drawer?.querySelector('[data-cart-drawer-message]');
  if (!messageElement) return;
  messageElement.textContent = message;
  messageElement.hidden = !message;
};

const cartDrawerEmptyMarkup = () => `
  <div class="cart-drawer__empty">
    <div class="cart-drawer__empty-icon" aria-hidden="true">
      <svg viewBox="0 0 48 48" focusable="false">
        <path d="M14.4 16.5h27.1l-3.2 14.8H17.2L14.4 16.5Z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
        <path d="M14.4 16.5 13 10H6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <circle cx="19.6" cy="38.2" r="2.4" fill="currentColor"/>
        <circle cx="35.5" cy="38.2" r="2.4" fill="currentColor"/>
        <path d="M22 22h11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </div>
    <p class="cart-drawer__empty-title">YOUR CART IS EMPTY</p>
    <a class="button button--full" href="/collections/colsigen-products">Continue Shopping</a>
    <div class="cart-drawer__account">
      <p><strong>Have an account?</strong></p>
      <p><a href="/account/login">Log in</a> to check out faster.</p>
    </div>
  </div>
`;

const cartDrawerRemoveButton = (label = 'Remove item') => `
  <button class="cart-drawer__remove" type="button" data-cart-remove aria-label="${escapeCartHtml(label)}">
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M9 3h6l1 2h4v2H4V5h4l1-2Zm1 7h2v8h-2v-8Zm4 0h2v8h-2v-8ZM7 8h10l-.8 12H7.8L7 8Z" fill="currentColor"/>
    </svg>
  </button>
`;

const renderCartDrawer = (cart) => {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  const body = drawer.querySelector('[data-cart-drawer-body], [data-cart-items]');
  const total = drawer.querySelector('[data-cart-drawer-total], [data-cart-subtotal]');
  const footer = drawer.querySelector('[data-cart-footer]');
  const title = drawer.querySelector('[data-cart-drawer-title]');
  document.querySelectorAll('[data-cart-count]').forEach((count) => {
    count.textContent = String(cart.item_count);
    count.hidden = cart.item_count === 0;
    count.setAttribute('aria-label', `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'} in cart`);
  });

  if (title) title.textContent = cart.item_count > 0 ? `YOUR CART (${cart.item_count})` : 'YOUR CART';
  if (total) total.textContent = formatCartMoney(cart.total_price);
  if (footer) footer.hidden = cart.item_count === 0;
  if (!body) return;

  if (!cart.items.length) {
    body.innerHTML = cartDrawerEmptyMarkup();
    return;
  }

  body.innerHTML = cart.items.map((item) => `
    <article class="cart-drawer__item" data-cart-item-key="${escapeCartHtml(item.key)}">
      <a class="cart-drawer__media" href="${item.url}">
        ${item.image ? `<img src="${escapeCartHtml(item.image)}" alt="${escapeCartHtml(item.product_title)}">` : ''}
      </a>
      <div class="cart-drawer__details">
        <a class="cart-drawer__title" href="${item.url}">${escapeCartHtml(item.product_title)}</a>
        ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-drawer__meta">${escapeCartHtml(item.variant_title)}</p>` : ''}
        <div class="cart-drawer__line-price">
          <span>${formatCartMoney(item.final_price)}</span>
          <strong>${formatCartMoney(item.final_line_price)}</strong>
        </div>
        <div class="cart-drawer__controls">
          <div class="quantity-stepper quantity-stepper--card quantity-stepper--cart" data-cart-line-controls>
            <button type="button" aria-label="Decrease quantity" data-cart-quantity-decrease>-</button>
            <label class="visually-hidden" for="CartDrawerQuantity-${escapeCartHtml(item.key)}">Quantity</label>
            <input id="CartDrawerQuantity-${escapeCartHtml(item.key)}" data-cart-quantity-input type="number" value="${item.quantity}" min="0" inputmode="numeric">
            <button type="button" aria-label="Increase quantity" data-cart-quantity-increase>+</button>
          </div>
          ${cartDrawerRemoveButton(`Remove ${item.product_title}`)}
        </div>
        <p class="cart-drawer__line-error" data-cart-line-error hidden></p>
      </div>
    </article>
  `).join('');
};

const changeCartDrawerItem = async (itemElement, quantity) => {
  const drawer = itemElement?.closest('[data-cart-drawer]');
  const key = itemElement?.dataset.cartItemKey;
  if (!drawer || !key) return;

  const nextQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);
  const lineError = itemElement.querySelector('[data-cart-line-error]');
  if (lineError) {
    lineError.hidden = true;
    lineError.textContent = '';
  }
  showCartDrawerMessage(drawer, '');
  setCartDrawerLoading(drawer, true);

  try {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity: nextQuantity }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) throw new Error(await response.text());
    const cart = await response.json();
    renderCartDrawer(cart);
  } catch (error) {
    const message = getCartErrorMessage(error);
    if (lineError) {
      lineError.textContent = message;
      lineError.hidden = false;
    } else {
      showCartDrawerMessage(drawer, message);
    }
  } finally {
    setCartDrawerLoading(drawer, false);
  }
};

const getCartErrorMessage = (error) => {
  try {
    const parsed = JSON.parse(String(error.message || error));
    return parsed.description || parsed.message || 'We could not add this product to cart.';
  } catch (parseError) {
    return 'We could not add this product to cart. Please review availability and try again.';
  }
};

const showProductFormMessage = (form, message) => {
  let messageElement = form.querySelector('[data-product-form-message]');
  if (!messageElement) {
    messageElement = document.createElement('p');
    messageElement.className = 'product-form-message';
    messageElement.dataset.productFormMessage = '';
    messageElement.setAttribute('role', 'alert');
    const anchor = form.querySelector('.product-form-actions') || form.querySelector('.product-card__quick-row');
    if (anchor) {
      anchor.after(messageElement);
    } else {
      form.append(messageElement);
    }
  }
  messageElement.textContent = message;
};

document.addEventListener('click', (event) => {
  const decrease = event.target.closest('[data-cart-quantity-decrease]');
  const increase = event.target.closest('[data-cart-quantity-increase]');
  const remove = event.target.closest('[data-cart-remove]');
  if (!decrease && !increase && !remove) return;

  const itemElement = event.target.closest('[data-cart-item-key]');
  const input = itemElement?.querySelector('[data-cart-quantity-input]');
  if (!itemElement || !input) return;

  const current = Number.parseInt(input.value || '0', 10) || 0;
  let nextQuantity = current;
  if (decrease) nextQuantity = Math.max(0, current - 1);
  if (increase) nextQuantity = current + 1;
  if (remove) nextQuantity = 0;
  input.value = String(nextQuantity);
  changeCartDrawerItem(itemElement, nextQuantity);
});

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-cart-quantity-input]');
  if (!input) return;
  const itemElement = input.closest('[data-cart-item-key]');
  if (!itemElement) return;
  const nextQuantity = Math.max(0, Number.parseInt(input.value || '0', 10) || 0);
  input.value = String(nextQuantity);
  changeCartDrawerItem(itemElement, nextQuantity);
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[action*="/cart/add"]');
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!form || !drawer) return;

  event.preventDefault();
  const submit = form.querySelector('[type="submit"]');
  const formMessage = form.querySelector('[data-product-form-message]');
  const addOnInputs = Array.from(document.querySelectorAll('[data-complementary-add-on]:checked'))
    .filter((input) => input.form === form && input.value);

  if (formMessage) formMessage.textContent = '';
  submit?.setAttribute('disabled', 'disabled');

  try {
    const formData = new FormData(form);

    if (addOnInputs.length) {
      const mainItem = {
        id: Number(formData.get('id')),
        quantity: Number(formData.get('quantity') || 1)
      };
      const sellingPlan = formData.get('selling_plan');

      if (sellingPlan) {
        mainItem.selling_plan = Number(sellingPlan);
      }

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify({
          items: [
            mainItem,
            ...addOnInputs.map((input) => ({
              id: Number(input.value),
              quantity: Number(input.dataset.quantity || 1)
            }))
          ]
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!addResponse.ok) {
        throw new Error(await addResponse.text());
      }
    } else {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' }
      });
      if (!addResponse.ok) {
        throw new Error(await addResponse.text());
      }
    }
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    renderCartDrawer(cart);
    setCartDrawerState(drawer, true);
  } catch (error) {
    showProductFormMessage(form, getCartErrorMessage(error));
  } finally {
    submit?.removeAttribute('disabled');
  }
});

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-subscription-tooltip-trigger]');
  const close = event.target.closest('[data-subscription-tooltip-close]');

  document.querySelectorAll('[data-subscription-tooltip].is-open').forEach((tooltip) => {
    if (close || !tooltip.contains(event.target)) {
      tooltip.classList.remove('is-open');
      tooltip.querySelector('[data-subscription-tooltip-trigger]')?.setAttribute('aria-expanded', 'false');
    }
  });

  if (trigger) {
    const tooltip = trigger.closest('[data-subscription-tooltip]');
    if (!tooltip) return;
    const isOpen = !tooltip.classList.contains('is-open');
    tooltip.classList.toggle('is-open', isOpen);
    trigger.setAttribute('aria-expanded', String(isOpen));
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('[data-subscription-tooltip].is-open').forEach((tooltip) => {
    tooltip.classList.remove('is-open');
    tooltip.querySelector('[data-subscription-tooltip-trigger]')?.setAttribute('aria-expanded', 'false');
  });
});

const backToTopButton = document.querySelector('[data-back-to-top]');

if (backToTopButton) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let backToTopTicking = false;

  const updateBackToTop = () => {
    backToTopButton.classList.toggle('is-visible', window.scrollY > 420);
    backToTopTicking = false;
  };

  document.addEventListener('scroll', () => {
    if (backToTopTicking) return;
    backToTopTicking = true;
    window.requestAnimationFrame(updateBackToTop);
  }, { passive: true });

  backToTopButton.addEventListener('click', () => {
    window.scrollTo({
      top: 0,
      behavior: reducedMotion.matches ? 'auto' : 'smooth'
    });
  });

  updateBackToTop();
}

const getMobileSectionSliderIndex = (slider) => {
  const slides = Array.from(slider?.children || []).filter((slide) => slide.offsetWidth > 0);
  if (!slider || !slides.length) return 0;

  const sliderRect = slider.getBoundingClientRect();
  const sliderCenter = sliderRect.left + (sliderRect.width / 2);

  return slides.reduce((closestIndex, slide, index) => {
    const currentRect = slides[closestIndex].getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const currentDistance = Math.abs((currentRect.left + currentRect.width / 2) - sliderCenter);
    const slideDistance = Math.abs((slideRect.left + slideRect.width / 2) - sliderCenter);
    return slideDistance < currentDistance ? index : closestIndex;
  }, 0);
};

const updateMobileSectionSliderProgress = (slider) => {
  const progress = slider?.parentElement?.querySelector('[data-product-mobile-section-progress]');
  const indicator = progress?.querySelector('span');
  const slides = Array.from(slider?.children || []).filter((slide) => slide.offsetWidth > 0);

  if (!progress || !indicator || slides.length < 2) {
    if (progress) progress.hidden = true;
    return;
  }

  progress.hidden = false;
  const index = getMobileSectionSliderIndex(slider);
  const width = 100 / slides.length;
  progress.style.setProperty('--product-mobile-progress-width', `${width}%`);
  indicator.style.left = `${width * index}%`;
};

const initializeMobileSectionSliders = () => {
  document.querySelectorAll('.product-formulation-grid, .product-steps__grid').forEach((slider) => {
    const progress = slider.parentElement?.querySelector('[data-product-mobile-section-progress]');
    if (!progress) return;

    updateMobileSectionSliderProgress(slider);

    if (slider.dataset.mobileSectionProgressInitialized === 'true') return;
    slider.dataset.mobileSectionProgressInitialized = 'true';

    let progressFrame = null;
    const requestProgressUpdate = () => {
      if (progressFrame) window.cancelAnimationFrame(progressFrame);
      progressFrame = window.requestAnimationFrame(() => {
        updateMobileSectionSliderProgress(slider);
        progressFrame = null;
      });
    };

    slider.addEventListener('scroll', requestProgressUpdate, { passive: true });
    window.addEventListener('resize', requestProgressUpdate, { passive: true });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeMobileSectionSliders();
  });
} else {
  initializeMobileSectionSliders();
}

document.addEventListener('shopify:section:load', initializeMobileSectionSliders);
document.addEventListener('shopify:block:select', initializeMobileSectionSliders);
